import { useContext } from "react";
import { AuthContext } from "../contexts/AuthContext";

/**
 * Custom hook for subscription management
 * Issue #9: Frontend State Management
 *
 * Provides easy access to subscription state and actions
 */
export const useSubscription = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useSubscription must be used within an AuthProvider");
  }

  const { auth, subscription } = context;

  return {
    // Subscription data
    subscription: subscription.data,
    loading: subscription.loading,
    error: subscription.error,
    hasAccess: subscription.hasAccess,
    plans: subscription.plans,
    plansLoading: subscription.plansLoading,

    // User data for subscription context
    user: auth.user,
    isAuthenticated: auth.isAuthenticated,
    isAdmin: auth.isAdmin,

    // Subscription actions
    refreshSubscription: subscription.refresh,
    activateTrial: subscription.activateTrial,
    checkAccess: subscription.checkAccess,
    invalidateCache: subscription.invalidateCache,
    fetchPlans: subscription.fetchPlans,

    // Utility functions
    getTrialDaysRemaining: () => {
      return subscription.data?.trial_days_remaining || 0;
    },

    isInTrial: () => {
      return subscription.data?.is_in_trial || false;
    },

    getSubscriptionStatus: () => {
      return subscription.data?.status || null;
    },

    getSubscriptionStatusDisplay: () => {
      return subscription.data?.status_display || "Unknown";
    },

    getSubscriptionStatusColor: () => {
      return subscription.data?.status_color || "secondary";
    },

    canActivateTrial: () => {
      return (
        auth.isAuthenticated && !subscription.data && !subscription.loading
      );
    },

    needsPayment: () => {
      const status = subscription.data?.status;
      return status === "past_due" || status === "unpaid";
    },

    isGracePeriod: () => {
      return subscription.data?.in_grace_period || false;
    },

    getAccessReason: () => {
      if (auth.isAdmin) return "Admin Access";
      if (subscription.data?.is_in_trial) return "Trial Period";
      if (subscription.data?.status === "active") return "Active Subscription";
      if (subscription.data?.in_grace_period) return "Grace Period";
      return "No Access";
    },
  };
};

export default useSubscription;
